import React, { useState } from "react";
import Title from "../components/common/Title";
import FilterSelector from "../components/gst-report/FilterSelector";
import { gstPayableReportFormData } from "../App";
import { useAuth } from "../context/authContext";
import Server from "../api/Server";
import notify from "devextreme/ui/notify";
import { formatProductResponse, getNavigatorLanguage } from "../utils";
import { alert } from "devextreme/ui/dialog";
import PayableReport from "../components/gst-report/PayableReport";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  subWeeks,
  subMonths,
  subYears,
} from "date-fns";

export let GSTReportData = [];

const GSTReport = () => {
  const [selectedItems, setSelectedItem] = useState([]);
  const [isSelected, setIsSelected] = useState(false);
  const [productsList, setProducts] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [reportStore, setReportStore] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [state, setState] = useState(gstPayableReportFormData);

  const { user } = useAuth();

  const onValueChanged = (event, name) => {
    const { value } = event;

    if (name === "searchString") {
      if (value === "") {
        setProducts([]);
        setIsEmpty(false);
        setIsSelected(true);
        setSearchString(value);
        return;
      } else {
        const request = {
          clientCode: user.clientCode,
          searchNameIncrementally: value,
          request: "getProducts",
          lang: user.lang,
        };

        if (user && (user.isIframe || user.sessionKey)) {
          request.sessionKey = user.sessionKey;
        }

        Server.searchNameIncrementally(request)
          .then((response) => {
            if (response.data.status.responseStatus === "error") {
              notify("Error getting product. Please try again", "error", 3000);
            } else {
              setProducts(formatProductResponse(response.data.records));

              setIsSelected(false);
              setIsEmpty(false);
            }
          })
          .catch((err) => {
            notify("Error getting product. Please try again", "error", 3000);
          });
        setSearchString(value);
      }
    } else if (name === "dateRange") {
      setQuickDate(value);
    } else {
      setState({ ...state, [name]: value });
    }
  };

  const setQuickDate = (value) => {
    if (value) {
      if (value === "today") {
        setState({
          ...state,
          dateRange: value,
          startDate: Date.now(),
          endDate: Date.now(),
        });
      }

      if (value === "current week") {
        const locale = getNavigatorLanguage();
        const start = startOfWeek(Date.now(), { locale });
        const end = endOfWeek(Date.now(), { locale });

        setState({
          ...state,
          dateRange: value,
          startDate: start,
          endDate: end,
        });
      }

      if (value === "current month") {
        const start = startOfMonth(Date.now());
        const end = endOfMonth(Date.now());

        setState({
          ...state,
          dateRange: value,
          startDate: start,
          endDate: end,
        });
      }

      if (value === "current year") {
        const start = startOfYear(Date.now());
        const end = endOfYear(Date.now());

        setState({
          ...state,
          dateRange: value,
          startDate: start,
          endDate: end,
        });
      }

      if (value === "yesterday") {
        setState({
          ...state,
          dateRange: value,
          startDate: subDays(Date.now(), 1),
          endDate: subDays(Date.now(), 1),
        });
      }

      if (value === "last week") {
        const week = subWeeks(Date.now(), 1);
        const locale = getNavigatorLanguage();
        const start = startOfWeek(week, { locale });
        const end = endOfWeek(week, { locale });
        setState({
          ...state,
          dateRange: value,
          startDate: start,
          endDate: end,
        });
      }

      if (value === "last month") {
        const month = subMonths(Date.now(), 1);
        const start = startOfMonth(month);
        const end = endOfMonth(month);
        setState({
          ...state,
          dateRange: value,
          startDate: start,
          endDate: end,
        });
      }

      if (value === "last year") {
        const year = subYears(Date.now(), 1);
        const start = startOfYear(year);
        const end = endOfYear(year);
        setState({
          ...state,
          dateRange: value,
          startDate: start,
          endDate: end,
        });
      }

      if (value === "last 15 days") {
        setState({
          ...state,
          dateRange: value,
          startDate: subDays(Date.now(), 14),
          endDate: Date.now(),
        });
      }

      if (value === "last 30 days") {
        setState({
          ...state,
          dateRange: value,
          startDate: subDays(Date.now(), 29),
          endDate: Date.now(),
        });
      }

      if (value === "last 60 days") {
        setState({
          ...state,
          dateRange: value,
          startDate: subDays(Date.now(), 59),
          endDate: Date.now(),
        });
      }

      if (value === "last 90 days") {
        setState({
          ...state,
          dateRange: value,
          startDate: subDays(Date.now(), 89),
          endDate: Date.now(),
        });
      }
    } else {
      setState({
        ...state,
        dateRange: value,
        startDate: null,
        endDate: null,
      });
    }
  };

  const onItemDeleted = (e) => {
    const { itemData } = e;
    const selected = [...selectedItems];
    const filtered = selected.filter(
      (item) => item.productID !== itemData.productID
    );

    if (filtered.length === 0) {
      setIsEmpty(true);
    }

    setSelectedItem(filtered);
  };

  const onSelectedItemsChange = (args) => {
    if (args.name === "selectedItems") {
      if (args.value.length === 0) return;

      const isExiting = selectedItems.find(
        (item) => item.productID === args.value[0].productID
      );

      if (isExiting) return;

      setSelectedItem([...selectedItems, args.value[0]]);

      setIsSelected(true);
      setSearchString("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { startDate, endDate, warehouseIds, customerIds } = state;

    if (!startDate || !endDate) {
      alert("Start date and End date are required", "Notification");
      return;
    }

    setIsLoading(true);
    const params = {
      startDateTime: `${format(startDate, "yyyy-MM-dd")} 00:00:00`,
      endDateTime: `${format(endDate, "yyyy-MM-dd")} 23:59:59`,
    };

    if (warehouseIds.length) {
      params.warehouseIds = warehouseIds.map((e) => e).toString();
    }

    if (customerIds.length) {
      params.customerIds = customerIds.map((e) => e).toString();
    }

    if (selectedItems.length) {
      params.productIds = selectedItems
        .map((product) => product.productID)
        .toString();
    }

    try {
      const { data } = await Server.getGSTPayableReport(params);

      setReportStore(data.data.items);
      GSTReportData = data.data.items;
      setIsLoading(false);
    } catch (err) {
      if (err.response) {
        const {
          response: { data },
        } = err;
        if (data) {
          notify(`${data.status.errorDescription}`, "error", 3000);
        } else {
          notify("Error getting reports data, please try again", "error", 3000);
        }
      } else {
        notify("Error getting reports data, please try again", "error", 3000);
      }

      setIsLoading(false);
    }
  };

  console.log(state);

  return (
    <section className="mt-4">
      <Title pageTitle={"Tax Payable Report"} />
      <FilterSelector
        formData={state}
        isSelected={isSelected}
        productsList={productsList}
        isEmpty={isEmpty}
        searchString={searchString}
        onValueChanged={onValueChanged}
        onItemDeleted={onItemDeleted}
        onSelectedItemsChange={onSelectedItemsChange}
        selectedItems={selectedItems}
        handleSubmit={handleSubmit}
      />
      <PayableReport reportStore={reportStore} isLoading={isLoading} />
    </section>
  );
};

export default GSTReport;
